import { FC, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Alert, Button, Loader } from '@app/ui'
import { useNavigate } from 'react-router-dom'
import { ROUTES } from '@app/constants'
import { AlertType, ButtonType, ButtonSize } from '@app/types'
import {
  useGetCurrentUserSubsLazyQuery,
  useStopUserSubscriptionMutation,
  useUnfollowChannelMutation,
  useAssignTipsterChannelMutation,
  useUpdateUserMutation,
  UserRoles,
  UserOnboardingStages,
} from '@app/graphql'
import { CurrentUserState } from '@app/storage'
import { useSnapshot } from 'valtio'
import { UserCacheHelper } from '@app/helpers/userCache'

interface ChangeUserRoleViewProps {
  open: boolean
  onClose: () => void
}

const ChangeUserRoleView: FC<ChangeUserRoleViewProps> = ({
  open,
  onClose
}) => {
  const navigate = useNavigate()
  const { id: userId, currentChannelId: channelId } = useSnapshot(CurrentUserState)
  const { t } = useTranslation()
  const [ getSubs ] = useGetCurrentUserSubsLazyQuery()
  const [stopSub] = useStopUserSubscriptionMutation()
  const [updateUser] = useUpdateUserMutation()
  const [unfollowChannel] = useUnfollowChannelMutation()
  const [assignChannel] = useAssignTipsterChannelMutation()
  const [loading, setLoading] = useState(false)

  const handlePromoteToTipster = async () => {
    setLoading(true)
    onClose()
    try {
      const { data } = await getSubs({
        variables: {
          includeExpired: false,
          limit: 100,
          skip: 0,
        },
        fetchPolicy: 'network-only',
      })
      const subscriptions = data?.serviceSubscriptionsByUserId?.edges?.map((edge) => edge.node) ?? []
      for (const subscription of subscriptions) {
        await stopSub({
          variables: {
            id: subscription.id,
          }
        })
      }
      const channelIds = Array.from(
        new Set([
          channelId,
          ...subscriptions
            .map((subscription) => subscription.channelId?.toString())
            .filter(Boolean),
        ]),
      )
      for (const channelId of channelIds) {
        await unfollowChannel({
          variables: {
            channelId,
          },
        })
      }
      await updateUser({
        variables: {
          input: {
            role: UserRoles.Tipster,
            onboardingStage: UserOnboardingStages.ChoosingFavoriteSports
          },
        },
      })
      for (const channelId of channelIds) {
        await assignChannel({
          variables: {
            channelId,
            tipsterId: userId,
          },
        })
      }
      UserCacheHelper.clear()
      console.log('User successfully promoted to TIPSTER')
      navigate(ROUTES.root)
    } catch (error) {
      console.error('Failed to promote user to TIPSTER:', error)
    } finally {
      setLoading(false)
    }
  }
  if(loading) return <Loader />
  return (
    <Alert
      warningMsg={t('promoteToTipster.warning')}
      alertType={AlertType.warning}
      open={open}
      onClose={onClose}
    >
      <Content>
        <h1 className="title">{ t('promoteToTipster.title')}</h1>
        <p className="body">
          {t('promoteToTipster.body1')}
        </p>
        <p className="body">{t('promoteToTipster.body2')}</p>
        <StyleButton
          size={ButtonSize.big}
          className="vb-font w700 white"
          type={ButtonType.primary}
          onClick={handlePromoteToTipster}
          fluid
        >{t('promoteToTipster.confirm')}</StyleButton>
        <StyleButton
          size={ButtonSize.big}
          className="vb-font w700 white"
          type={ButtonType.transparent}
          fluid
          border="1.5px solid var(--button-border-color)"
          onClick={onClose}
        >{t('common.cancel')}</StyleButton>
      </Content>
    </Alert>
  )
}

const StyleButton = styled(Button)`
  padding: 16px !important;
  border-radius: 8px !important;
  font-weight: 600;
  font-size: 14px;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  margin-bottom: 16px;
  &.type-primary {
    background: var(--bg-quinary);
    color: white;
  }
  [data-theme="light"] &.type-transparent {
    color: var(--text-primary);
  }
`

const Content = styled.div`
  margin-top: 15px;
    .title,.body {
      text-align: center;
      margin-bottom: 20px;
    }
    .title {
      padding-bottom: 10px;
    }
  `

export default ChangeUserRoleView
